import React, {
  useEffect,
  useState,
  useMemo,
  createContext,
  useContext,
} from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Button,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Stack,
  Drawer,
  IconButton,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";

import { Sun, Moon } from "lucide-react";

// Accent colors
const PRIMARY_COLOR_LIGHT = "#3FA2F6";
const PRIMARY_COLOR_DARK = "#60a5fa";


const ColorModeContext = createContext({ toggleColorMode: () => {} });

const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
      mode,
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === "dark" ? PRIMARY_COLOR_DARK : PRIMARY_COLOR_LIGHT,
          },
          background: {
            default: mode === "dark" ? "#020617" : "#f1f5f9",
            paper: mode === "dark" ? "#0f172a" : "#ffffff",
          },
          text: {
            primary: mode === "dark" ? "#e5e7eb" : "#1e293b",
            secondary: mode === "dark" ? "#94a3b8" : "#64748b",
          },
          divider: mode === "dark" ? "rgba(31,41,55,1)" : "rgba(203,213,225,1)",
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

// Status dot
const StatusChip = ({ active }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: "999px",
      bgcolor: active ? "#22c55e" : "#f97373",
      mx: "auto",
    }}
  />
);

const AdminPanelContent = () => {
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const PRIMARY_COLOR =
    mode === "dark" ? PRIMARY_COLOR_DARK : PRIMARY_COLOR_LIGHT;

  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    sap_username: "",
  });
  const [agentAccess, setAgentAccess] = useState({
    hr: [],
    sales: [],
    finance: [],
    purchase: [],
    inventory: [],
    palmtop: [],
  });

  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const AGENT_OPTIONS = {
    hr: ["chat"],
    sales: ["chat", "analytics", "upload"],
    finance: ["chat", "analytics"],
    purchase: ["chat", "analytics", "upload"],
    inventory: ["chat", "analytics"],
    palmtop: ["chat"],
  };

  const fetchUsers = async () => {
    const { data } = await supabase
      .from("user_profiles")
      .select("email, sap_username, agents, created_at");

    setUsers(data || []);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setNewUser({ email: "", password: "", sap_username: "" });
    setAgentAccess({
      hr: [],
      sales: [],
      finance: [],
      purchase: [],
      inventory: [],
      palmtop: [],
    });
  };

  const openCreateForm = () => {
    setEditingUser(null);
    resetForm();
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
    setEditingUser(null);
  };

  const toggleAgentPermission = (agent, permission) => {
    setAgentAccess((prev) => {
      const has = prev[agent]?.includes(permission);
      return {
        ...prev,
        [agent]: has
          ? prev[agent].filter((p) => p !== permission)
          : [...prev[agent], permission],
      };
    });
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.password)
      return alert("Email & password required.");

    setLoading(true);
    try {
      const response = await fetch(
        "https://cd9d-205-254-163-120.ngrok-free.app/api/create_user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: newUser.email,
            password: newUser.password,
            sap_username: newUser.sap_username,
            agents: agentAccess,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      alert("User created.");
      closeForm();
      fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditUser = (u) => {
    setEditingUser(u);
    setNewUser({
      email: u.email,
      password: "",
      sap_username: u.sap_username,
    });
    setAgentAccess(u.agents || agentAccess);
    setShowForm(true);
  };

  const saveUserChanges = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://cd9d-205-254-163-120.ngrok-free.app/api/update_user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: newUser.email,
            sap_username: newUser.sap_username,
            agents: agentAccess,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      alert("User updated!");
      closeForm();
      fetchUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Delete ${email}?`)) return;

    try {
      const res = await fetch(
        "https://cd9d-205-254-163-120.ngrok-free.app/api/delete_user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await res.json();
      if (!res.ok) return alert(data.error);

      alert("User deleted.");
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/Ale-x";
  };

  // ----------------------------
  // JSX UI starts here
  // ----------------------------
  return (
    <Box sx={{ p: 4 }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Typography variant="h5" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
          Ale-X Admin Panel
        </Typography>

        <Stack direction="row" spacing={2}>
          <IconButton onClick={toggleColorMode}>
            {mode === "dark" ? <Sun /> : <Moon />}
          </IconButton>

          <Button variant="contained" onClick={openCreateForm}>
            Add User
          </Button>

          <Button variant="outlined" onClick={logout}>
            Logout
          </Button>
        </Stack>
      </Box>

      {/* USER LIST */}
      <Typography sx={{ mb: 2 }} variant="h6">
        Existing Users
      </Typography>

      {users.map((u) => (
        <Paper key={u.email} sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{u.email}</Typography>
              <Typography sx={{ fontSize: "0.8rem" }}>
                SAP Username: {u.sap_username || "-"}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => startEditUser(u)}>
                Edit
              </Button>
              <Button color="error" variant="contained" onClick={() => deleteUser(u.email)}>
                Delete
              </Button>
            </Stack>
          </Box>

          {/* PERMISSIONS TABLE */}
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell sx={{ textAlign: "center" }}>Chat💬</TableCell>
                <TableCell sx={{ textAlign: "center" }}>Analytics📊</TableCell>
                <TableCell sx={{ textAlign: "center" }}>Upload⬆️</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(AGENT_OPTIONS).map(([agent, perms]) => {
                const userPerms = u.agents?.[agent] || [];
                return (
                  <TableRow key={agent}>
                    <TableCell>{agent.toUpperCase()}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {perms.includes("chat") ? (
                        <StatusChip active={userPerms.includes("chat")} />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {perms.includes("analytics") ? (
                        <StatusChip active={userPerms.includes("analytics")} />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {perms.includes("upload") ? (
                        <StatusChip active={userPerms.includes("upload")} />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ))}

      {/* FORM DRAWER */}
      <Drawer anchor="right" open={showForm} onClose={closeForm}>
        <Box sx={{ p: 3, width: 380 }}>
          <Typography variant="h6" sx={{ color: PRIMARY_COLOR, mb: 2 }}>
            {editingUser ? "Edit User" : "Create User"}
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Email"
              disabled={!!editingUser}
              value={newUser.email}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, email: e.target.value }))
              }
            />

            {!editingUser && (
              <TextField
                type="password"
                label="Password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, password: e.target.value }))
                }
              />
            )}

            <TextField
              label="SAP Username"
              value={newUser.sap_username}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, sap_username: e.target.value }))
              }
            />
          </Stack>

          <Typography sx={{ mt: 3, mb: 1 }}>Agent Access</Typography>

          {Object.entries(AGENT_OPTIONS).map(([agent, perms]) => (
            <Box key={agent} sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>{agent.toUpperCase()}</Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {perms.map((perm) => (
                  <Button
                    key={perm}
                    variant={
                      agentAccess[agent]?.includes(perm)
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => toggleAgentPermission(agent, perm)}
                  >
                    {perm}
                  </Button>
                ))}
              </Stack>
            </Box>
          ))}

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            onClick={editingUser ? saveUserChanges : addUser}
            disabled={loading}
          >
            {loading ? "Saving..." : editingUser ? "Save Changes" : "Create User"}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

// MAIN WRAPPER
const AdminPanel = () => (
  <ThemeModeProvider>
    <AdminPanelContent />
  </ThemeModeProvider>
);

export default AdminPanel;
